import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import Hls from 'hls.js';

@Component({
  selector: 'app-video-player',
  standalone: false,
  templateUrl: './video-player.component.html',
  styleUrl: './video-player.component.scss',
})
export class VideoPlayerComponent
  implements AfterViewInit, OnDestroy {

  @Input() requireFullscreen = true;
  @Input() hideVideo = true;
  @Input() allowVideoFastForward = false;
  @Input() videoProgressSeconds = 0;

  @Input() videoUrl: string | null = null;
  @Input() hlsUrl: string | null = null;

  @Output() progressChanged = new EventEmitter<number>();
  @Output() videoEnded = new EventEmitter<void>();
  @Output() durationChanged = new EventEmitter<string>();

  @ViewChild('videoPlayer', { static: false })
  private videoPlayer!: ElementRef<HTMLVideoElement>;

  isVideoPlaying = false;
  videoDuration: string | null = null;

  private fullscreenChangeHandler!: () => void;
  private hls?: Hls;

  ngAfterViewInit(): void {
    this.initializeVideo();

    const videoElement = this.videoPlayer.nativeElement;

    videoElement.addEventListener('loadedmetadata', () => {
      const durationInSeconds = videoElement.duration;

      const hours = Math.floor(durationInSeconds / 3600);
      const minutes = Math.floor(
        (durationInSeconds % 3600) / 60
      );

      this.videoDuration =
        `${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
      this.durationChanged.emit(this.videoDuration);

      if (this.videoProgressSeconds >= durationInSeconds - 1) {
        this.videoProgressSeconds = 0;
      }

      videoElement.currentTime = this.videoProgressSeconds;
    });

    videoElement.onended = () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }

      this.isVideoPlaying = false;
      this.videoEnded.emit();
    };

    if (!this.requireFullscreen) {
      this.setSeekingRestriction(videoElement);
    }
  }

  playVideo(): void {
    const videoElement = this.videoPlayer.nativeElement;

    if (this.requireFullscreen) {
      this.fullscreenChangeHandler = () => {
        if (!document.fullscreenElement) {
          videoElement.pause();
          this.isVideoPlaying = false;
        }
      };

      document.addEventListener(
        'fullscreenchange',
        this.fullscreenChangeHandler
      );
    }

    if (!this.allowVideoFastForward) {
      this.setSeekingRestriction(videoElement);
    } else {
      videoElement.onseeking = null;
    }

    videoElement.play();

    if (this.requireFullscreen) {
      videoElement.requestFullscreen();
    }

    this.isVideoPlaying = true;
  }

  updatePlayTime(): void {
    const videoElement =
      this.videoPlayer?.nativeElement;

    if (!videoElement) {
      return;
    }

    if (
      videoElement.currentTime >
      this.videoProgressSeconds
    ) {
      this.videoProgressSeconds =
        videoElement.currentTime;

      this.progressChanged.emit(
        this.videoProgressSeconds
      );
    }
  }

  ngOnDestroy(): void {
    this.hls?.destroy();

    if (this.fullscreenChangeHandler) {
      document.removeEventListener(
        'fullscreenchange',
        this.fullscreenChangeHandler
      );
    }

    const videoElement =
      this.videoPlayer?.nativeElement;

    if (videoElement) {
      videoElement.pause();
      videoElement.onseeking = null;
      videoElement.onended = null;
    }
  }

  private setSeekingRestriction(
    videoElement: HTMLVideoElement
  ): void {
    videoElement.onseeking = () => {
      if (
        videoElement.currentTime >
        this.videoProgressSeconds
      ) {
        videoElement.currentTime =
          this.videoProgressSeconds;
      }
    };
  }

  private initializeVideo(): void {
    if (!this.videoPlayer) {
      return;
    }

    const videoElement = this.videoPlayer.nativeElement;

    this.hls?.destroy();

    if (this.hlsUrl) {
      if (Hls.isSupported()) {
        this.hls = new Hls();

        this.hls.loadSource(this.hlsUrl);
        this.hls.attachMedia(videoElement);

        return;
      }

      if (
        videoElement.canPlayType(
          'application/vnd.apple.mpegurl'
        )
      ) {
        videoElement.src = this.hlsUrl;
        return;
      }
    }

    videoElement.src = this.videoUrl ?? '';
  }
}